import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Table, Badge, Row, Col } from 'react-bootstrap';
import { FaPlus } from 'react-icons/fa';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { listarContasElegiveis, listarVinculosContaUsuario } from '~/Services/Service';

const ModalVincularContas = ({
  aberto,
  gruposEmpresa,
  usuariosAtivos,
  onVincular,
  onFechar,
}) => {
  const [codigoGE, setCodigoGE] = useState(null);
  const [codigoUsuario, setCodigoUsuario] = useState(null);
  const [contas, setContas] = useState([]);
  const [selecionadas, setSelecionadas] = useState(new Set());
  const [loadingContas, setLoadingContas] = useState(false);
  const [filtroPesquisa, setFiltroPesquisa] = useState('');

  // Reset ao abrir
  useEffect(() => {
    if (aberto) {
      setCodigoGE(null);
      setCodigoUsuario(null);
      setContas([]);
      setSelecionadas(new Set());
      setFiltroPesquisa('');
    }
  }, [aberto]);

  // Carrega contas disponíveis somente quando GE + Usuário estão selecionados
  // e filtra as que já estão vinculadas ao usuário
  useEffect(() => {
    let ativo = true;
    setContas([]);
    setSelecionadas(new Set());
    setFiltroPesquisa('');

    if (!codigoGE || !codigoUsuario) return undefined;

    const carregar = async () => {
      setLoadingContas(true);
      try {
        const [elegiveis, vinculos] = await Promise.all([
          listarContasElegiveis(codigoGE, null),
          listarVinculosContaUsuario(codigoGE, codigoUsuario),
        ]);
        if (!ativo) return;

        const codigosJaVinculados = new Set(
          (vinculos || []).map((v) => v.codigo_conta)
        );

        const disponiveis = (elegiveis || []).filter(
          (c) => !codigosJaVinculados.has(c.codigo_conta)
        );

        setContas(disponiveis);
      } catch (err) {
        if (ativo) toast.error('Erro ao carregar contas disponíveis.');
      } finally {
        if (ativo) setLoadingContas(false);
      }
    };
    carregar();
    return () => {
      ativo = false;
    };
  }, [codigoGE, codigoUsuario]);

  const opcoesGE = (gruposEmpresa || []).map((ge) => ({
    value: ge.codigo_grupo_empresa,
    label: ge.codigo_grupo_empresa + ' - ' + ge.nome,
  }));

  const opcoesUsuarios = (usuariosAtivos || []).map((u) => ({
    value: u.codigo_usuario,
    label: u.codigo_usuario + ' - ' + u.nome,
  }));

  const valorGE = opcoesGE.find((o) => o.value === codigoGE) || null;
  const valorUsuario = opcoesUsuarios.find((o) => o.value === codigoUsuario) || null;

  const termoBusca = (filtroPesquisa || '').trim().toLowerCase();
  const contasFiltradas = termoBusca
    ? contas.filter((c) => {
        const nome = (c.nome || '').toLowerCase();
        const codigo = String(c.codigo_conta || '');
        return nome.indexOf(termoBusca) !== -1 || codigo.indexOf(termoBusca) !== -1;
      })
    : contas;

  const todasMarcadas = contasFiltradas.length > 0
    && contasFiltradas.every((c) => selecionadas.has(c.codigo_conta));

  const handleChangeGE = (opcao) => {
    setCodigoGE(opcao ? opcao.value : null);
    setCodigoUsuario(null);
  };

  const handleChangeUsuario = (opcao) => {
    setCodigoUsuario(opcao ? opcao.value : null);
  };

  const handleToggle = (codigoConta) => {
    setSelecionadas((prev) => {
      const novo = new Set(prev);
      if (novo.has(codigoConta)) {
        novo.delete(codigoConta);
      } else {
        novo.add(codigoConta);
      }
      return novo;
    });
  };

  const handleToggleAll = () => {
    setSelecionadas((prev) => {
      const novo = new Set(prev);
      if (todasMarcadas) {
        contasFiltradas.forEach((c) => novo.delete(c.codigo_conta));
      } else {
        contasFiltradas.forEach((c) => novo.add(c.codigo_conta));
      }
      return novo;
    });
  };

  const handleVincular = () => {
    if (!codigoGE) {
      toast.warn('Selecione um Grupo de Empresa.');
      return;
    }
    if (!codigoUsuario) {
      toast.warn('Selecione um usuário.');
      return;
    }
    if (selecionadas.size === 0) {
      toast.warn('Selecione ao menos uma conta.');
      return;
    }
    onVincular(Array.from(selecionadas), codigoGE, codigoUsuario);
  };

  const renderGridContas = () => {
    if (!codigoGE || !codigoUsuario) {
      return (
        <p className="text-muted text-center py-3">
          Selecione o Grupo de Empresa e o Usuário para exibir as contas disponíveis.
        </p>
      );
    }
    if (loadingContas) {
      return <p className="text-muted text-center py-3">Carregando contas...</p>;
    }
    if (contas.length === 0) {
      return (
        <p className="text-muted text-center py-3">
          Todas as contas elegíveis já estão vinculadas a este usuário.
        </p>
      );
    }
    return (
      <>
        <Form.Control
          type="text"
          size="sm"
          placeholder="Pesquisar por código ou nome da conta..."
          value={filtroPesquisa}
          onChange={(e) => setFiltroPesquisa(e.target.value)}
          className="mb-2"
        />
        <div
          style={{
            maxHeight: '280px',
            overflowY: 'auto',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
          }}
        >
          <Table striped bordered hover size="sm" className="mb-0">
            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
              <tr>
                <th style={{ width: '44px', textAlign: 'center' }}>
                  <Form.Check
                    type="checkbox"
                    checked={todasMarcadas}
                    onChange={handleToggleAll}
                    title="Selecionar todas"
                  />
                </th>
                <th style={{ width: '90px' }}>Código</th>
                <th>Nome</th>
                <th style={{ width: '140px' }}>Tipo</th>
              </tr>
            </thead>
            <tbody>
              {contasFiltradas.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-muted text-center py-2">
                    Nenhuma conta encontrada para "{filtroPesquisa}".
                  </td>
                </tr>
              ) : (
                contasFiltradas.map((conta) => (
                  <tr
                    key={conta.codigo_conta}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleToggle(conta.codigo_conta)}
                  >
                    <td
                      style={{ textAlign: 'center' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Form.Check
                        type="checkbox"
                        checked={selecionadas.has(conta.codigo_conta)}
                        onChange={() => handleToggle(conta.codigo_conta)}
                      />
                    </td>
                    <td>{conta.codigo_conta}</td>
                    <td>{conta.nome || '-'}</td>
                    <td>{conta.tipo_descricao || conta.tipo || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </>
    );
  };

  return (
    <Modal show={aberto} onHide={onFechar} centered size="lg">
      <Modal.Header
        closeButton
        style={{ backgroundColor: '#0747a6', color: '#fff' }}
      >
        <Modal.Title style={{ fontSize: '1rem', color: '#fff' }}>
          Vincular Contas ao Usuário
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row className="mb-3">
          <Col md={6} className="mb-2 mb-md-0">
            <Form.Label className="mb-1" style={{ fontSize: '0.85rem', fontWeight: 600 }}>
              Grupo de Empresa
            </Form.Label>
            <Select
              placeholder="Selecione o GE..."
              options={opcoesGE}
              value={valorGE}
              onChange={handleChangeGE}
              isClearable
              components={{ DropdownIndicator }}
              styles={customStyles}
            />
          </Col>
          <Col md={6}>
            <Form.Label className="mb-1" style={{ fontSize: '0.85rem', fontWeight: 600 }}>
              Usuário
            </Form.Label>
            <Select
              placeholder={codigoGE ? 'Selecione o usuário...' : 'Selecione um Grupo de Empresa'}
              options={opcoesUsuarios}
              value={valorUsuario}
              onChange={handleChangeUsuario}
              isClearable
              isDisabled={!codigoGE}
              components={{ DropdownIndicator }}
              styles={customStyles}
            />
          </Col>
        </Row>

        <div className="d-flex justify-content-between align-items-center mb-1">
          <Form.Label className="mb-0" style={{ fontSize: '0.85rem', fontWeight: 600 }}>
            Contas disponíveis
          </Form.Label>
          {selecionadas.size > 0 && (
            <Badge variant="primary">{selecionadas.size} selecionada(s)</Badge>
          )}
        </div>

        {renderGridContas()}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onFechar}>
          Cancelar
        </Button>
        <Button
          variant="success"
          size="sm"
          onClick={handleVincular}
        >
          <FaPlus className="mr-1" />
          Vincular {selecionadas.size > 0 ? selecionadas.size + ' conta(s)' : ''}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalVincularContas;
